/** localStorage wrapper with 'bttf_' key prefix. */

const PREFIX = "bttf_"

const DEFAULT_MUSIC_VOLUME = 0.3
const DEFAULT_SFX_VOLUME = 0.7

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback
  try {
    const raw = localStorage.getItem(PREFIX + key)
    if (raw === null) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value))
  } catch {
    // Storage full or unavailable — silently ignore.
  }
}

export interface LeaderboardEntry {
  score: number
  hero: string
  wavesReached: number
  dangerLevel: number
  date: string
}

const DEFAULT_UNLOCKED_HEROES = ["bella", "krmelo"]

export const PersistedData = {
  getHighScores(): number[] {
    return read<number[]>("highScores", [])
  },

  addHighScore(score: number): void {
    const scores = PersistedData.getHighScores()
    scores.push(score)
    scores.sort((a, b) => b - a)
    write("highScores", scores.slice(0, 10))
  },

  getMusicVolume(): number {
    return read<number>("musicVolume", DEFAULT_MUSIC_VOLUME)
  },

  setMusicVolume(v: number): void {
    write("musicVolume", Math.max(0, Math.min(1, v)))
  },

  getSfxVolume(): number {
    return read<number>("sfxVolume", DEFAULT_SFX_VOLUME)
  },

  setSfxVolume(v: number): void {
    write("sfxVolume", Math.max(0, Math.min(1, v)))
  },

  isMusicMuted(): boolean {
    return read<boolean>("musicMuted", false)
  },

  setMusicMuted(m: boolean): void {
    write("musicMuted", m)
  },

  isStorySeen(): boolean {
    return read<boolean>("storySeen", false)
  },

  setStorySeen(s: boolean): void {
    write("storySeen", s)
  },

  isTutorialSeen(): boolean {
    return read<boolean>("tutorialSeen", false)
  },

  setTutorialSeen(s: boolean): void {
    write("tutorialSeen", s)
  },

  getUnlockedHeroes(): string[] {
    return read<string[]>("unlockedHeroes", DEFAULT_UNLOCKED_HEROES)
  },

  unlockHero(heroId: string): boolean {
    const heroes = PersistedData.getUnlockedHeroes()
    if (heroes.includes(heroId)) return false
    heroes.push(heroId)
    write("unlockedHeroes", heroes)
    return true
  },

  isHeroUnlocked(heroId: string): boolean {
    return PersistedData.getUnlockedHeroes().includes(heroId)
  },

  getDangerLevel(): number {
    return read<number>("dangerLevel", 0)
  },

  setDangerLevel(level: number): void {
    write("dangerLevel", Math.max(0, Math.min(5, level)))
  },

  getMaxDangerUnlocked(): number {
    return read<number>("maxDangerUnlocked", 0)
  },

  setMaxDangerUnlocked(level: number): void {
    write("maxDangerUnlocked", Math.max(0, Math.min(5, level)))
  },

  getLeaderboard(): LeaderboardEntry[] {
    return read<LeaderboardEntry[]>("leaderboard", [])
  },

  addLeaderboardEntry(entry: LeaderboardEntry): void {
    const board = PersistedData.getLeaderboard()
    board.push(entry)
    board.sort((a, b) => b.score - a.score)
    write("leaderboard", board.slice(0, 10))
  },

  getBestWaveReached(): number {
    return read<number>("bestWaveReached", 0)
  },

  setBestWaveReached(wave: number): void {
    const current = PersistedData.getBestWaveReached()
    if (wave > current) write("bestWaveReached", wave)
  },
}
